import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { configureApi } from '@ai-content-hub/shared-api';
import { NotificationProvider } from './components/common';
import {
    AuthProvider,
    adoptActingTenantFromUrl,
    authClient,
    getActingTenantId,
    usesSso,
} from './auth';
import App from './App';
import { ThemeInitializer } from './components/ThemeInitializer';
import { resolveH5pStaticBase } from './lib/h5pStaticBase';
import './styles/globals.css';

// Set global app version (injected at build time)
window.APP_VERSION = typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : 'dev';

// EAA-1694: the acting tenant BEFORE the first request, not in a React effect.
//
// The embed runs on its own origin, so its localStorage carries no picker choice and the
// `X-Tenant-Id` header was omitted — which, for an identity with more than one membership,
// means no acting tenant and a blanket ERROR_PERMISSION_DENIED (upload included). The host
// app already names the tenant in the embed URL; this reads it. Placed above
// `configureApi` because the earliest calls fire during initial render.
adoptActingTenantFromUrl();

// Configure shared API before rendering (token loaded from localStorage for studio + uploads)
/**
 * EAA-1694: the editor's API credential.
 *
 * The SSO access token wins when there is a live session, and the hand-pasted API token in
 * `localStorage` remains the fallback. Both are kept on purpose: `getAuthToken` is called
 * for embed/player routes too, where there is no interactive session and never was, and the
 * editor is also run standalone against environments with no Keycloak.
 *
 * `getAccessTokenSync` rather than `getAccessToken` because this builder is called from
 * outside React while assembling headers and cannot `await`. The shared client keeps that
 * value current from its own userLoaded/userUnloaded events, so a silently-renewed token is
 * picked up without this code doing anything; a 401 after expiry is handled by
 * `onUnauthorized` below rather than by blocking every request on a promise.
 */
configureApi({
    baseUrl: import.meta.env.VITE_API_URL || 'http://localhost:8000/api',
    h5pUrl: import.meta.env.VITE_H5P_URL || '',
    /**
     * EAA-755: set the static H5P base HERE, at module scope, not only from the
     * embed pages' effects.
     *
     * `fetchH5PPlayerModel` reads it from shared-api's config and prefers
     * `<base>/manifests/<Ubername>.json` over the H5P server. The embed pages do
     * pass it (via `configureContentApi`, which forwards to `configureApi`), but
     * they do so in a PARENT effect — and React runs child effects first, so
     * `H5PPlayer` had already fetched the model while the base was still unset.
     * Playback then went to the H5P server, whose style list does not include
     * `overrides/ach-player.css`, so H5P content rendered with its stock light
     * palette inside a dark player no matter what the host theme said.
     *
     * Setting it before React mounts means no effect ordering can lose it. A
     * per-embed `?h5pStatic=` override still wins: the embed pages reconfigure
     * with their own resolved value afterwards.
     */
    h5pStaticBase: resolveH5pStaticBase() || undefined,
    getAuthToken: () => {
        if (usesSso) {
            const ssoToken = authClient.getAccessTokenSync();
            if (ssoToken) {
                return ssoToken;
            }
        }
        try {
            return localStorage.getItem('editor_upload_api_token');
        } catch {
            return null;
        }
    },
    // EAA-1694: names the acting tenant. Required for an SSO identity with more than one
    // membership — the backend refuses to guess and answers 403 without it. Returns null
    // for API-token callers, whose tenant is carried in the credential itself, so the
    // header is simply omitted there.
    getActingTenantId: () => (usesSso ? getActingTenantId() : null),
    // EAA-1682 shape: refresh once on a 401 and retry, instead of failing a request made
    // from a long-idle tab.
    onUnauthorized: () => (usesSso ? authClient.refreshToken() : Promise.resolve(null)),
});

// Register all content types before rendering
import { registerAllContentTypes, contentTypeVersions } from './content-types';
registerAllContentTypes();

// Expose content-type versions globally
window.CONTENT_TYPE_VERSIONS = contentTypeVersions;

// Restore upload settings from localStorage so uploads work before user visits ContentListPage
const win = typeof window !== 'undefined' ? (window as unknown as { __UPLOAD_GET_HEADERS__?: () => Record<string, string>; __UPLOAD_ENDPOINT__?: string }) : null;
if (win) {
    // EAA-1694: the H5P/upload bridge reads its headers through this hook. Resolved per
    // call rather than captured once, so a token renewed mid-session is used instead of a
    // stale one, and so an SSO session that starts after page load is picked up at all.
    // The acting tenant rides along for the same reason it does on the API client.
    win.__UPLOAD_GET_HEADERS__ = () => {
        const ssoToken = usesSso ? authClient.getAccessTokenSync() : null;
        const fallback = (() => {
            try {
                return localStorage.getItem('editor_upload_api_token');
            } catch {
                return null;
            }
        })();
        const token = ssoToken || fallback;
        if (!token) {
            return {};
        }
        const headers: Record<string, string> = {
            Authorization:
                ssoToken || token.startsWith('eyJ') ? `Bearer ${token}` : `Token ${token}`,
        };
        const actingTenantId = usesSso ? getActingTenantId() : null;
        if (actingTenantId) {
            headers['X-Tenant-Id'] = actingTenantId;
        }
        return headers;
    };
    const storedBaseUrl = localStorage.getItem('editor_upload_base_url');
    if (storedBaseUrl) {
        const base = storedBaseUrl.replace(/\/+$/, '');
        win.__UPLOAD_ENDPOINT__ = base.includes('/api') ? `${base}/upload` : `${base}/api/upload`;
    }
}

// Get base URL for router (set during build via VITE_BASE_URL env var)
const basename = import.meta.env.BASE_URL || '/';

ReactDOM.createRoot(document.getElementById('root')!).render(
    <React.StrictMode>
        <BrowserRouter basename={basename}>
            <ThemeInitializer />
            <NotificationProvider>
                <AuthProvider>
                    <App />
                </AuthProvider>
            </NotificationProvider>
        </BrowserRouter>
    </React.StrictMode>
);
